// src/pages/DashboardCursosDisponibles.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Clock, Calendar, BookOpen } from 'lucide-react';
import { getCatalog } from '../services/groupCoursesApi';

const DashboardCursosDisponibles = () => {
  const navigate = useNavigate();

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getCatalog({});
      setCourses(response.courses || []);
    } catch (err) {
      console.error('Error cargando programas:', err);
      setError('No se pudieron cargar los programas. Intenta de nuevo más tarde.');
    } finally {
      setLoading(false);
    }
  };

  const handleCourseClick = (courseId) => {
    navigate(`/group-courses/${courseId}`);
  };

  const CourseCard = ({ course }) => (
    <div
      onClick={() => handleCourseClick(course.courseId)}
      className="group bg-white rounded-xl border border-gray-200 hover:border-green-300 hover:shadow-lg transition-all duration-200 cursor-pointer p-8 max-w-2xl"
    >
      {/* Título + badge */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <h3 className="text-2xl font-semibold text-gray-900 group-hover:text-green-700 transition-colors leading-snug">
          {course.title}
        </h3>
        <span className="flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
          style={{ background: '#E8F7EE', color: '#1E8E5A' }}>
          <span className="w-1.5 h-1.5 rounded-full bg-current" />
          Activo
        </span>
      </div>

      <p className="text-sm text-gray-500 leading-relaxed mb-6">
        {course.shortDescription}
      </p>

      <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-gray-500 mb-7">
        <span className="flex items-center gap-1.5">
          <Clock className="w-4 h-4 text-gray-400" />
          {course.estimatedHours || 0}h estimadas
        </span>
        <span className="flex items-center gap-1.5">
          <Calendar className="w-4 h-4 text-gray-400" />
          {course.totalWeeks || 18} semanas
        </span>
        <span className="flex items-center gap-1.5">
          <Users className="w-4 h-4 text-gray-400" />
          {course.groupConfig?.minGroupSize || 3}–{course.groupConfig?.maxGroupSize || 8} personas
        </span>
      </div>

      <div className="flex justify-end">
        <button
          className="px-6 py-3 rounded-lg text-sm font-medium text-white transition-opacity hover:opacity-90"
          style={{ background: '#1E8E5A' }}
        >
          Entrar al programa
        </button>
      </div>
    </div>
  );

  return (
    <div className="p-6 md:p-8">
      <div className="max-w-5xl">
        {/* Header */}
        <div className="mb-10">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Programas</h1>
          <p className="text-base text-gray-600 max-w-2xl leading-relaxed">
            Entrenamientos colaborativos diseñados para aprender mediante proyectos, simulaciones y experimentación.
          </p>
        </div>

        {/* Contenido */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-gray-200 border-t-green-600 rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-5 text-center">
            <p className="text-sm text-red-700 mb-3">{error}</p>
            <button
              onClick={loadCourses}
              className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Reintentar
            </button>
          </div>
        ) : courses.length === 0 ? (
          <div className="text-center py-16">
            <BookOpen className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">Aún no hay programas publicados disponibles.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 max-w-4xl">
            {courses.map((course) => (
              <CourseCard key={course.courseId} course={course} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardCursosDisponibles;
